import type { UseQueryOptions } from "@pinia/colada";
import type { RouteLocationNormalized } from "vue-router";

import { useQueryCache } from "@pinia/colada";
import { defineNuxtRouteMiddleware } from "#app/composables/router";

import { localeToPathPrefix } from "~/app/routes.helpers.ts";
import { usePerformancePage } from "~/features/performance/api/cms.api.ts";
import {
  useProductInfoPage,
  useProductPage,
} from "~/features/product/api/cms.api.ts";
import { useStaticPage } from "~/features/static/api/cms.api.ts";
import { useHost } from "~/shared/composables/useHost.ts";
import { statamicPreviewFromQuery } from "~/shared/composables/useStatamicPreview.ts";
import { hostAndUrlToLocale } from "~/shared/locale/hostLocale.ts";

// Per page, so each keeps its own data type
function pageEntry<TData, TError>(options: UseQueryOptions<TData, TError>) {
  const queryCache = useQueryCache();
  const entry = queryCache.ensure(options);
  return {
    data: () => entry.state.value.data,
    refresh: () => queryCache.refresh(entry),
  };
}

// The page's own query for `to`: same params as the page passes → same cache entry
function cmsPageEntry(to: RouteLocationNormalized) {
  const locale = hostAndUrlToLocale(useHost(), to.path);
  const preview = statamicPreviewFromQuery(to.query);
  const { slug, geoProductSlug, infoSlug } = to.params;

  if (to.name === "mainSite.geoProduct" && typeof slug === "string") {
    return pageEntry(useProductPage({ locale, uri: `/${slug}` }, preview));
  }
  if (
    to.name === "mainSite.geoProduct.info" &&
    typeof geoProductSlug === "string" &&
    typeof infoSlug === "string"
  ) {
    return pageEntry(
      useProductInfoPage(
        { locale, uri: `/${geoProductSlug}/${infoSlug}` },
        preview,
      ),
    );
  }
  if (to.name === "mainSite.staticPage") {
    const uri = to.path.slice(localeToPathPrefix(locale).length);
    return pageEntry(useStaticPage({ locale, uri }, preview));
  }
  if (to.name === "mainSite.performance") {
    const uri = to.path.slice(localeToPathPrefix(locale).length);
    return pageEntry(usePerformancePage({ locale, uri }, preview));
  }
  return null;
}

// Focus page is a CMS toggle, but the layout has to know it before render: app.vue renders the
// layout before the page's query resolves.
export default defineNuxtRouteMiddleware(async (to, from) => {
  const entry = cmsPageEntry(to);
  if (!entry) {
    return;
  }

  // Server: the page's own request, which SSR waited for anyway. Hydration reads the entry the
  // server sent. Client navigation never waits: uncached, it keeps the current variant and
  // useFocusPageLayout sets the page's once the data is in.
  if (import.meta.server) {
    // Leaves the entry in error state → the page renders the error itself
    await entry.refresh().catch(() => null);
  }

  const data = entry.data();
  to.meta.focusPage = data ? data.general.focusPage : from.meta.focusPage;
});
